/**
 * Service layer — Permits domain (Airtable-backed)
 *
 * Owns the permit tracking chain:
 *   Project → Permits (tbl1cwgaxtfeiycqP)
 *           → Agency Approvals / "agency reviews" (tbl2Yu7YtDZ6tFFhI)
 *           → Permit Activity (tblLO4mxHR04VCk5v)
 *
 * STATUS IS AIRTABLE-DRIVEN. Logging a Permit Activity event triggers an
 * Airtable automation that sets the Agency Approval Status; the Permit-level
 * Status and the "ball" formula then recompute. This service therefore only
 * LOGS events and READS the automation-computed status — it never writes a
 * status field directly. After logging, callers re-fetch the review to show
 * the new status.
 *
 * Child records are fetched by record id (the parent's link arrays return real
 * recXXX ids per the Airtable API rules) or by the project UID lookup chain —
 * never by ARRAYJOIN on a link field (which returns primary values, not ids).
 */

import { apiGet, apiGetAll, apiPost, apiPatch, apiDelete } from './_api.js'
import { POST_REVISION_TYPE_IDS, PERMIT_MS_TYPE_IDS } from '../outline/constants.js'

const TBL_PERMITS   = 'tbl1cwgaxtfeiycqP'
const TBL_REVIEWS   = 'tbl2Yu7YtDZ6tFFhI'   // Agency Approvals
const TBL_ACTIVITY  = 'tblLO4mxHR04VCk5v'   // Permit Activity
const TBL_DELTAS    = 'tbl2sdOXCdvS2R5DL'   // Delta Revisions
const TBL_DEL       = 'tblFuiDpahqCL4eIK'   // Project Deliverables (milestones)

/** First value of a possibly-array Airtable field. */
function first(v) {
  if (Array.isArray(v)) return v.length ? v[0] : null
  return v == null ? null : v
}

/**
 * Permit Activity "Activity Type" options (exact strings — must match the
 * singleSelect so the status automation fires). Order mirrors the workflow.
 */
export const PERMIT_ACTIVITY_TYPES = [
  '🔬 Requested Pre-Submittal Peer Review',
  '⚙️ Received Pre-Submittal Peer Review',
  '🏛️ Submitted to Agency',
  'ℹ️ Agency Has Requested Items',
  '💵 Paid Fees',
  '🚀 Agency Has Confirmed Review Commenced',
  '📋 Received Agency Comments',
  '♺ Resubmitted to Agency',
  '📅 Hearing Date Scheduled',
  '🚩 Posted Noticing Materials',
  '⏭️ Received Hearing Continuance',
  '👍 Received Committee Approval',
  '☑️ End of Appeal Period - No Appeals',
  '❌ End of Appeal Period - Appeals Filed',
  '👍 Received Agency Approval',
  '🔍 Status Update',
  '🏛️ Submitted Permit Revision to Agency',
  '📋 Received Agency Corrections for Permit Revision',
  '♺ Resubmitted Permit Revision to Agency',
  '👍 Agency Approved Permit Revision',
]

/** Permit "Type of Permit" options (exact strings) — shared by AddPermitModal and the post window's
 *  inline "add the agency review" step. */
export const PERMIT_TYPES = [
  'Building Permit', 'Conditional Use Permit', 'Grading Permit', 'Health Permit',
  'Revocable Encroachment Permit', 'Pool Spa Permit', 'Landscape Hardscape Permit',
  'Demolition Permit', 'Sewer Connection Permit', 'Site Development Permit',
  'Solar Permit', 'Temporary Use Permit', 'Coastal Commission Development Permit',
  'Coastal Commission Exemption',
]

/** Permit Activity "Planned Next Activity" options (exact strings). */
export const PERMIT_PLANNED_NEXT = [
  'Receive Peer Review',
  'Submit to Agency',
  'Receive Agency Instructions',
  'Pay Fees',
  'Receive Agency Corrections',
  'Resubmit to Agency',
  'Post Noticing Materials',
  'Attend Hearing',
  'Receive Agency Approval',
  'Check Status',
]

/**
 * Activity Type → Agency Review Status. Logging one of these events sets the
 * review status in-app (migration-ready; mirrors the intent of the Airtable
 * automation). Types not listed here leave the status unchanged.
 * Status strings must match the Agency Approval "Status" singleSelect exactly.
 */
export const PERMIT_ACTIVITY_TO_STATUS = {
  // Pre-submittal peer review cycle
  '🔬 Requested Pre-Submittal Peer Review':            'Under Review',
  '⚙️ Received Pre-Submittal Peer Review':             'Preparing to Submit',
  // Main submission cycle
  '🏛️ Submitted to Agency':                            'Submitted - Intake in Process',
  '🚀 Agency Has Confirmed Review Commenced':           'Under Review',
  'ℹ️ Agency Has Requested Items':                      'Items Requested',
  '📋 Received Agency Comments':                         'Making Corrections',
  '♺ Resubmitted to Agency':                            'Under Review',
  // Hearing cycle
  '📅 Hearing Date Scheduled':                           'Preparing for Hearing',
  '🚩 Posted Noticing Materials':                        'Preparing for Hearing',
  '⏭️ Received Hearing Continuance':                    'Making Corrections',
  '👍 Received Committee Approval':                      'Appeal Period',
  '☑️ End of Appeal Period - No Appeals':               'Approved',
  // Approval
  '👍 Received Agency Approval':                         'Approved',
  // Permit Revision track
  '🏛️ Submitted Permit Revision to Agency':             'Permit Revision - Under Review',
  '📋 Received Agency Corrections for Permit Revision':  'Permit Revision - Making Corrections',
  '♺ Resubmitted Permit Revision to Agency':            'Permit Revision - Under Review',
  '👍 Agency Approved Permit Revision':                  'Permit Revision - Approved',
  // No status change: 💵 Paid Fees, ❌ End of Appeal Period - Appeals Filed, 🔍 Status Update.
}

/**
 * Agency-review statuses where the ball is in OUR court (we owe the next move).
 * Mirrors the permit-level "Agency Review Ball" formula in Airtable.
 */
const BALL_STATUSES = new Set([
  'Preparing to Submit',
  'Items Requested',
  'Making Corrections',
  'Preparing for Hearing',
  'Permit Revision - Making Corrections',
])

/** True when an agency-review status means we owe the next action. */
export function reviewHasBall(status) {
  return !status || BALL_STATUSES.has(status)
}

/**
 * Fetch all Permits for a project, normalized for the UI.
 * @param {string} projectId — Project record id
 * @returns {Promise<object[]>}
 */
export async function getProjectPermits(projectId) {
  const formula = 'FIND("' + projectId + '",ARRAYJOIN({Project Record UID (from Project)},","))'
  const records = await apiGet(
    TBL_PERMITS,
    'filterByFormula=' + encodeURIComponent(formula)
    + '&sort%5B0%5D%5Bfield%5D=Type+of+Permit&sort%5B0%5D%5Bdirection%5D=asc'
    + '&maxRecords=100'
  )
  return records.map(normalizePermit)
}

function normalizePermit(r) {
  const f = r.fields || {}
  const ballText = f['Agency Review Ball'] || ''
  return {
    id:                 r.id,
    name:               f['Name'] || f['Type of Permit'] || 'Permit',
    type:               f['Type of Permit'] || '',
    subProjectName:     f['Sub Project Name'] || '',
    trackingNumber:     f['Permit Tracking Number'] || '',
    status:             f['Status'] || '',           // emoji roll-up formula
    hasBall:            ballText.indexOf('⚽') !== -1,
    ballText,
    estimatedApproval:  f['Estimated Approval'] || null,
    issueDate:          f['Permit Issue Date'] || null,
    expirationDate:     f['Permit Expiration Date'] || null,
    webPortalUrl:       first(f['Web Portal']) || '',
    reviewIds:          Array.isArray(f['Agency Approvals']) ? f['Agency Approvals'] : [],
    slackChannelId:     first(f['Slack Channel ID (from Project)']) || '',
  }
}

/**
 * Fetch all Agency Reviews for a project, normalized and tagged with permitId.
 * Callers group by `permitId` to nest reviews under their permit.
 * @param {string} projectId — Project record id
 * @returns {Promise<object[]>}
 */
export async function getProjectAgencyReviews(projectId) {
  const formula = 'FIND("' + projectId + '",ARRAYJOIN({Project Record UID (from Project) (from Permits)},","))'
  const records = await apiGetAll(
    TBL_REVIEWS,
    'filterByFormula=' + encodeURIComponent(formula) + '&maxRecords=200'
  )
  return records.map(normalizeReview)
}

function normalizeReview(r) {
  const f = r.fields || {}
  const status = first(f['Status']) || ''
  return {
    id:                r.id,
    permitId:          first(f['Permits']) || null,
    name:              f['Name'] || first(f['Agency Text']) || 'Agency Review',
    status,
    hasBall:           reviewHasBall(status),
    trackingNumber:    f['Agency Tracking Number'] || '',
    agency:            first(f['Agency Text']) || '',
    agencyOrgId:       first(f['Agency']) || '',
    agencyType:        first(f['Agency Type (from Agency)']) || '',
    nextDeadline:      f['Next Agency Deadline'] || null,
    daysUntilDeadline: typeof f['Days Until Deadline'] === 'number' ? f['Days Until Deadline'] : null,
    dateSubmitted:     f['Date Submitted (Manual)'] || f['Date Submitted (Auto)'] || null,
    dateApproved:      f['Date Approved'] || null,
    latestActivity:    first(f['Activity (From Latest Permit Activity)']) || '',
    portalUrl:         first(f['Online Portal URL (from Agency)']) || '',
    activityIds:       Array.isArray(f['Permit Activity']) ? f['Permit Activity'] : [],
    slackThreadTs:     f['Slack Thread Timestamp'] || '',
    // The submission milestone that generated this review (e.g. "...Discretionary Approvals
    // Plan Check Drawings" / "...Permit Plan Check Drawings") — used to infer the permit's
    // DAPC/PPC/Plan-Check track when suggesting which revision milestone a new Delta belongs on.
    // See getRevisionMilestoneCandidates() below.
    submissionMsName:  first(f['Name (from Project Deliverables)']) || '',
    // Milestone(s) this review shows under (PermitsSection's "Permits" sub-section, gated by
    // PERMIT_MS_TYPES) — a multipleRecordLinks field, so real ids per Airtable API rules. This is
    // NEVER auto-populated by the app; a review only appears under a milestone once someone links
    // it explicitly (AddAgencyReviewModal's milestone picker). Empty here means exactly that never
    // happened yet, not a query bug — confirmed 2026-07 against a real review with none set.
    milestoneIds:      Array.isArray(f['Project Deliverables']) ? f['Project Deliverables'] : [],
  }
}

/**
 * Fetch Permit Activity records by id (newest first).
 * Pass an agency review's `activityIds` array.
 * @param {string[]} activityIds
 * @returns {Promise<object[]>}
 */
export async function getPermitActivity(activityIds) {
  if (!activityIds || !activityIds.length) return []
  const ors = activityIds.map(id => "RECORD_ID()='" + id + "'").join(',')
  const formula = 'OR(' + ors + ')'
  const records = await apiGet(
    TBL_ACTIVITY,
    'filterByFormula=' + encodeURIComponent(formula)
    + '&sort%5B0%5D%5Bfield%5D=Date&sort%5B0%5D%5Bdirection%5D=desc'
    + '&maxRecords=100'
  )
  return records.map(normalizeActivity)
}

function normalizeActivity(r) {
  const f = r.fields || {}
  return {
    id:               r.id,
    type:             f['Activity Type'] || '',
    date:             f['Date'] || null,
    remarks:          f['Remarks'] || '',
    recordedByName:   first(f['Member Name (from Recorded By)']) || '',
    plannedNext:      f['Planned Next Activity'] || '',
    plannedNextDate:  f['Planned Next Activity Date'] || null,
    nextComplete:     !!f['Next Activity Complete'],
    agencyContact:    first(f['Name (from Agency Contact)']) || '',
  }
}

/**
 * Log a Permit Activity event against an agency review, then:
 *  1. Set the review Status in-app from PERMIT_ACTIVITY_TO_STATUS (app-driven;
 *     matches the Airtable automation so both converge on the same value).
 *     Status is left unchanged for activity types with no mapping.
 *  2. If the activity is "Submitted to Agency", also check the parent Permit's
 *     `Application(s) Submitted` checkbox = true (mirrors the second automation action).
 *
 * Returns the created Permit Activity record.
 *
 * @param {string} reviewId — Agency Approval (agency review) record id
 * @param {string} activityType — one of PERMIT_ACTIVITY_TYPES
 * @param {object} [opts]
 * @param {string} [opts.date] — ISO date (YYYY-MM-DD); defaults to today
 * @param {string} [opts.remarks] — free text / rich text notes
 * @param {string} [opts.recordedByMemberId] — Members record id
 * @param {string} [opts.plannedNext] — one of PERMIT_PLANNED_NEXT
 * @param {string} [opts.plannedNextDate] — ISO date
 * @param {string} [opts.agencyContactId] — Agency Contact record id
 * @param {string} [opts.permitId] — parent Permit record id (needed for side-effects on the Permit)
 */
export async function logPermitActivity(reviewId, activityType, opts = {}) {
  const fields = {
    'Agency Approval': [reviewId],
    'Activity Type':   activityType,
    'Date':            opts.date || new Date().toISOString().slice(0, 10),
  }
  if (opts.remarks)            fields['Remarks'] = opts.remarks
  if (opts.recordedByMemberId) fields['Recorded By'] = [opts.recordedByMemberId]
  if (opts.plannedNext)        fields['Planned Next Activity'] = opts.plannedNext
  if (opts.plannedNextDate)    fields['Planned Next Activity Date'] = opts.plannedNextDate
  if (opts.agencyContactId)    fields['Agency Contact'] = [opts.agencyContactId]
  const created = await apiPost(TBL_ACTIVITY, fields)
  // 1) Set review status from the explicit map (mirrors automation intent)
  const nextStatus = PERMIT_ACTIVITY_TO_STATUS[activityType]
  if (nextStatus) await apiPatch(TBL_REVIEWS, reviewId, { 'Status': nextStatus })
  // 2) "Submitted to Agency" also checks Application(s) Submitted on the parent Permit
  if (activityType === '🏛️ Submitted to Agency' && opts.permitId) {
    await apiPatch(TBL_PERMITS, opts.permitId, { 'Application(s) Submitted': true })
  }
  return created
}

/** Manually set an agency review's Status (override outside the activity flow). */
export function setReviewStatus(reviewId, status) {
  return apiPatch(TBL_REVIEWS, reviewId, { 'Status': status })
}

/* ── Create / edit / delete ─────────────────────────────────────────────────── */

/**
 * Create a Permit under a project.
 * @param {string} projectId — Airtable Project record id
 * @param {object} fields — { typeOfPermit, trackingNumber, estimatedApproval }
 */
export function createPermit(projectId, fields = {}) {
  const f = { 'Project': [projectId] }
  if (fields.typeOfPermit)      f['Type of Permit'] = fields.typeOfPermit
  if (fields.subProjectName)    f['Sub Project Name'] = fields.subProjectName
  if (fields.trackingNumber)    f['Permit Tracking Number'] = fields.trackingNumber
  if (fields.estimatedApproval) f['Estimated Approval'] = fields.estimatedApproval
  return apiPost(TBL_PERMITS, f)
}

/** Patch a Permit. fields keyed by Airtable field name. */
export function updatePermit(permitId, fields) {
  return apiPatch(TBL_PERMITS, permitId, fields)
}

/** Delete a Permit. */
export function deletePermit(permitId) {
  return apiDelete(TBL_PERMITS, permitId)
}

/**
 * Create an Agency Review under a permit.
 * @param {string} permitId — Permit record id
 * @param {object} fields — { agencyOrgId, trackingNumber, status, milestoneIds }
 */
export function createAgencyReview(permitId, fields = {}) {
  const f = { 'Permits': [permitId], 'Status': fields.status || 'Preparing to Submit' }
  if (fields.agencyOrgId)    f['Agency'] = [fields.agencyOrgId]
  if (fields.trackingNumber) f['Agency Tracking Number'] = fields.trackingNumber
  if (fields.milestoneIds && fields.milestoneIds.length) f['Project Deliverables'] = fields.milestoneIds
  return apiPost(TBL_REVIEWS, f)
}

/**
 * Candidate milestones an Agency Review can show under (PermitsSection's "Permits"
 * sub-section — see PERMIT_MS_TYPE_IDS). Every project milestone whose deliverable type is
 * one of PERMIT_MS_TYPE_IDS; no "already linked elsewhere" exclusion — unlike revision
 * milestones, a review can legitimately show under several (an initial submission
 * milestone AND a later resubmission milestone both showing the same ongoing review).
 *
 * Filtered CLIENT-SIDE by deliverable-type id (2026-09-22 — was a server-side FIND() against
 * the Name lookup, which breaks silently on a rename; per this app's own Airtable API rule,
 * FIND() on a linked field's ARRAYJOIN returns primary values, not record ids, so id-based
 * matching can't be pushed into the formula anyway — only the project-scope stays server-side).
 * @param {string} projectId
 * @returns {Promise<{id: string, name: string}[]>}
 */
export async function getPermitMilestoneCandidates(projectId) {
  const formula = 'FIND("' + projectId + '",ARRAYJOIN({Project Record UID from Project},","))'
  const records = await apiGetAll(
    TBL_DEL,
    'filterByFormula=' + encodeURIComponent(formula) + '&maxRecords=500'
  )
  return records
    .filter(r => PERMIT_MS_TYPE_IDS.indexOf((r.fields['Deliverable'] || [])[0]) !== -1)
    .map(r => ({ id: r.id, name: r.fields['Full Deliverable Name'] || r.fields['Name'] || 'Milestone' }))
}

/** Patch an Agency Review. fields keyed by Airtable field name. */
export function updateAgencyReview(reviewId, fields) {
  return apiPatch(TBL_REVIEWS, reviewId, fields)
}

/** Delete an Agency Review. */
export function deleteAgencyReview(reviewId) {
  return apiDelete(TBL_REVIEWS, reviewId)
}

/** Patch a Permit Activity. fields keyed by Airtable field name. */
export function updatePermitActivity(activityId, fields) {
  return apiPatch(TBL_ACTIVITY, activityId, fields)
}

/** Delete a Permit Activity. */
export function deletePermitActivity(activityId) {
  return apiDelete(TBL_ACTIVITY, activityId)
}

/**
 * Fetch all Delta Revisions (both Plan Check and Permit Revision types) for a
 * project, normalized and tagged with `permitId` + `reviewIds`. Callers group
 * by `permitId` client-side — mirrors getProjectPermits/getProjectAgencyReviews.
 *
 * A Delta Revision belongs to exactly one Permit (`Permit` link,
 * prefersSingleRecordLink) but may reference SEVERAL Agency Reviews
 * (`Agency Plan Checks` is an explicit multi-link) — a single revision can
 * resolve comments from more than one agency at once (e.g. Planning + Building
 * + Fire on one submission). That's why this lives at the Permit level, not
 * nested under one Agency Review.
 * @param {string} projectId — Project record id
 * @returns {Promise<object[]>}
 */
export async function getDeltaRevisionsForProject(projectId) {
  const formula = 'FIND("' + projectId + '",ARRAYJOIN({Project Record ID (from Permit)},","))'
  const records = await apiGetAll(
    TBL_DELTAS,
    'filterByFormula=' + encodeURIComponent(formula) + '&maxRecords=200'
  )
  return records.map(normalizeDeltaRevision)
}

function normalizeDeltaRevision(r) {
  const f = r.fields || {}
  const type = first(f['Type']) || f['Type'] || ''
  return {
    id:               r.id,
    name:             f['Name'] || '',
    revisionLongName: first(f['Revision Long Name']) || f['Name'] || '',
    deltaNumber:      type === 'Permit Revision' ? (f['Permit Delta Number'] || '') : (f['Plan Check Delta Number'] || ''),
    type,
    description:      f['Description'] || '',
    releaseDate:      f['Release Date'] || null,
    permitId:         first(f['Permit']) || null,
    reviewIds:        Array.isArray(f['Agency Plan Checks']) ? f['Agency Plan Checks'] : [],
    // Reverse of the milestone-side `Delta Revisions - Plan Check`/`- Permit` link this
    // delta was attached to (createDeltaRevision writes that side; Airtable's own two-way
    // link keeps this side in sync) — whichever of the two fields applies to `type`.
    milestoneId:      type === 'Permit Revision'
      ? (first(f['Project Deliverables Permit Revision']) || null)
      : (first(f['Project Deliverables Plan Check Revision']) || null),
  }
}

/* ── Creating a Delta Revision + linking it to a revision milestone ──────────
   See docs/PLAN_CHECK.md ("P6 — Delta pop-off valve", 2026-07). A Delta must
   attach to a "commensurate" revision milestone (Project Deliverables whose
   deliverable type is one of POST_REVISION_TYPES) — Plan Check Revisions link
   via `Delta Revisions - Plan Check`, Permit Revisions via `Delta Revisions -
   Permit`. Milestones are pre-seeded once per phase by the WBS template, so
   "no candidate" usually means every existing revision milestone for that
   track already has a Delta — the caller falls back to creating a new one via
   the standard AddMilestoneWizard, then re-queries candidates. ─────────────── */

const DELTA_TYPE_MS_FIELD = {
  'Plan Check Revision': 'Delta Revisions - Plan Check',
  'Permit Revision':     'Delta Revisions - Permit',
}

/** Infer a rough "track" key from a deliverable-type name, for matching a
 * permit's own submission track (DAPC / PPC / plain Plan Check) against
 * candidate revision milestones. Best-effort string matching — not a stored
 * field — so a change to milestone naming could silently stop matching;
 * getRevisionMilestoneCandidates() always falls back to the full candidate
 * list when nothing matches, so a naming drift degrades to "show everything"
 * rather than "show nothing." */
function trackKeyForName(name) {
  if (!name) return null
  if (name.indexOf('Discretionary Approvals') !== -1 || /\bDAPC\b/.test(name)) return 'DAPC'
  if (name.indexOf('Permit Plan Check') !== -1 || /\bPPC\b/.test(name)) return 'PPC'
  if (name.indexOf('Plan Check') !== -1) return 'PLAN_CHECK'
  return null
}

/**
 * Candidate milestones a new Delta Revision of `deltaType` could attach to:
 * every project milestone whose deliverable type is a POST_REVISION_TYPES
 * revision milestone, excluding any that already have that Delta field
 * filled. `smart` narrows to whichever DAPC/PPC/Plan-Check track the permit's
 * own Agency Reviews belong to (via each review's `submissionMsName`) —
 * falls back to the full list when the track can't be inferred or nothing
 * matches, so the user is never left with zero options.
 * @param {string} projectId
 * @param {'Plan Check Revision'|'Permit Revision'} deltaType
 * @param {object[]} reviews — the permit's normalized Agency Reviews (for track inference)
 * @returns {Promise<{ smart: {id,name,typeName}[], all: {id,name,typeName}[] }>}
 */
export async function getRevisionMilestoneCandidates(projectId, deltaType, reviews) {
  const deltaField = DELTA_TYPE_MS_FIELD[deltaType]
  // Project-scope only stays server-side (id-based matching against the Deliverable link field
  // can't be pushed into an Airtable FIND()/ARRAYJOIN formula reliably — see the API rule note
  // on getPermitMilestoneCandidates above); the revision-type filter runs client-side by id.
  const formula = 'FIND("' + projectId + '",ARRAYJOIN({Project Record UID from Project},","))'
  const records = await apiGetAll(
    TBL_DEL,
    'filterByFormula=' + encodeURIComponent(formula) + '&maxRecords=500'
  )
  const all = records
    .filter(r => POST_REVISION_TYPE_IDS.indexOf((r.fields['Deliverable'] || [])[0]) !== -1)
    .filter(r => !((r.fields[deltaField] || []).length))
    .map(r => ({
      id:       r.id,
      name:     r.fields['Full Deliverable Name'] || r.fields['Name'] || 'Milestone',
      typeName: (r.fields['Name (from Deliverable)'] || [])[0] || '',
    }))
  const reviewTracks = new Set(
    (reviews || []).map(rv => trackKeyForName(rv.submissionMsName)).filter(Boolean)
  )
  const smart = reviewTracks.size ? all.filter(m => reviewTracks.has(trackKeyForName(m.typeName))) : []
  return { smart: smart.length ? smart : all, all }
}

/**
 * Retroactively link an existing Delta Revision to a milestone — for deltas
 * created before this linking existed (or via postMilestone's own picker,
 * whose write this mirrors). Same non-fatal-vs-caller contract as
 * createDeltaRevision: throws only on the actual write failing.
 * @param {'Plan Check Revision'|'Permit Revision'} type
 * @param {string} deltaId
 * @param {string} milestoneId
 */
export async function linkDeltaRevisionToMilestone(type, deltaId, milestoneId) {
  const msField = DELTA_TYPE_MS_FIELD[type]
  const res = await apiPatch(TBL_DEL, milestoneId, { [msField]: [deltaId] })
  if (res.error) throw new Error(res.error)
}

function nextDeltaLetter(count) {
  let n = count + 1
  let s = ''
  while (n > 0) {
    const rem = (n - 1) % 26
    s = String.fromCharCode(65 + rem) + s
    n = Math.floor((n - 1) / 26)
  }
  return s
}

/**
 * Create a new Delta Revision under a permit and (optionally) link it to a
 * revision milestone. Auto-numbers: Plan Check deltas get the next letter
 * (A, B, C…), Permit deltas the next number (1, 2, 3…), counted among the
 * permit's existing deltas of that type. The milestone link is a second,
 * non-fatal step (mirrors the ASI-creation pattern in AddMilestoneWizard) —
 * a link failure still leaves a valid, usable Delta; it's just unattached.
 * @param {object} opts
 * @param {string} opts.projectId
 * @param {string} opts.permitId
 * @param {'Plan Check Revision'|'Permit Revision'} opts.type
 * @param {string[]} [opts.reviewIds] — Agency Review record ids (Agency Plan Checks)
 * @param {string} [opts.milestoneId] — revision milestone to link
 * @param {string} [opts.description]
 * @returns {Promise<{ id: string, deltaNumber: string, milestoneLinkFailed: boolean }>}
 */
export async function createDeltaRevision({ projectId, permitId, type, reviewIds, milestoneId, description }) {
  const existing = await getDeltaRevisionsForProject(projectId)
  const countForPermit = existing.filter(d => d.permitId === permitId && d.type === type).length
  const numberField = type === 'Permit Revision' ? 'Permit Delta Number' : 'Plan Check Delta Number'
  const deltaNumber = type === 'Permit Revision' ? String(countForPermit + 1) : nextDeltaLetter(countForPermit)

  const fields = { 'Permit': [permitId], 'Type': type, [numberField]: deltaNumber }
  if (reviewIds && reviewIds.length) fields['Agency Plan Checks'] = reviewIds
  if (description) fields['Description'] = description

  const created = await apiPost(TBL_DELTAS, fields)
  if (created.error) throw new Error(created.error)

  let milestoneLinkFailed = false
  if (milestoneId) {
    const msField = DELTA_TYPE_MS_FIELD[type]
    const patchRes = await apiPatch(TBL_DEL, milestoneId, { [msField]: [created.id] })
    if (patchRes.error) milestoneLinkFailed = true
  }
  return { id: created.id, deltaNumber, milestoneLinkFailed }
}

/* ── Plan Check Letter tables (Airtable, read-only) ─────────────────────── */

const TBL_AT_PC_LETTERS   = 'tblR3VmXtHS01pso5'
const TBL_AT_PC_RESPONSES = 'tblvf0AwovOm7616X'

/**
 * Fetch Airtable Plan Check Revisions Letters linked to an agency review.
 * Filters via the `Agency Approval Record ID (from Agency Review)` lookup field.
 * Returns normalized letter objects (_source: 'airtable') ready to merge with
 * Supabase plan_check_letters.
 */
export async function getAirtableLettersForReview(reviewId) {
  const formula = 'FIND("' + reviewId + '",ARRAYJOIN({Agency Approval Record ID (from Agency Review)},","))'
  const records = await apiGetAll(
    TBL_AT_PC_LETTERS,
    'filterByFormula=' + encodeURIComponent(formula) + '&maxRecords=100'
  )
  return records.map(r => {
    const f = r.fields || {}
    return {
      id:                    r.id,
      _source:               'airtable',
      date_received:         f['Date Received'] || null,
      posted:                !!f['Posted'],
      response_letter_date:  f['Response Letter Date'] || null,
      source_letter_url:     f['Plan Check Letter Google Drive URL'] || null,
      response_letter_url:   Array.isArray(f['Response Letter']) && f['Response Letter'][0]
        ? f['Response Letter'][0].url : null,
      reviewer_contact_name: null,
      notes:                 null,
      round_id:              null,
      // Optional manual field — not on this Airtable table until added in the
      // Airtable UI (schema-write needs manual setup; see docs/PLAN_CHECK.md).
      // Undefined until then, so this is a harmless no-op in the meantime.
      label:                 f['Label'] || null,
      _responseIds: Array.isArray(f['Plan Check Revisions Letter Responses'])
        ? f['Plan Check Revisions Letter Responses'] : [],
      comments: undefined,
    }
  })
}

/**
 * Fetch Airtable Plan Check Revisions Letter Responses for a letter.
 * @param {string[]} responseIds — record IDs from the letter's linked responses field
 */
export async function getAirtableCommentsForLetter(responseIds) {
  if (!responseIds || !responseIds.length) return []
  const orClauses = responseIds.map(id => "RECORD_ID()='" + id + "'").join(',')
  const formula = 'OR(' + orClauses + ')'
  const records = await apiGetAll(
    TBL_AT_PC_RESPONSES,
    'filterByFormula=' + encodeURIComponent(formula) +
    '&sort%5B0%5D%5Bfield%5D=Comment+Number&sort%5B0%5D%5Bdirection%5D=asc'
  )
  return records.map(r => {
    const f = r.fields || {}
    return {
      id:                 r.id,
      _source:            'airtable',
      type:               f['Type'] === 'Redline Comment' ? 'redline' : 'letter',
      discipline:         f['Discipline'] || null,
      comment_number:     f['Comment Number'] != null ? String(f['Comment Number']) : null,
      subject:            f['Subject'] || null,
      comment_text:       f['Comment'] || null,
      response_text:      f['Response'] || null,
      response_finalized: !!f['Response Finalized'],
      cleared:            false,
      drawing_sheet_url:  null,
      redline_image_url:  null,
      sort_order:         null,
    }
  })
}

/**
 * Write back our response (and finalized flag) for an Airtable plan check comment.
 * @param {string} commentId — tblvf0AwovOm7616X record ID
 * @param {{ response?: string|null, responseFinalized?: boolean }} fields
 */
export async function updateAirtableCommentResponse(commentId, { response, responseFinalized } = {}) {
  const fields = {}
  if (response !== undefined)         fields['Response']          = response
  if (responseFinalized !== undefined) fields['Response Finalized'] = !!responseFinalized
  return apiPatch(TBL_AT_PC_RESPONSES, commentId, fields)
}

/**
 * Cross-project triage: every agency review where we currently have the ball,
 * across all projects. Used by the permit board and the /overdue command.
 * @returns {Promise<object[]>} normalized reviews (with permitId + project info)
 */
export async function getBallInCourtReviews() {
  // Open statuses where we owe the next move. Excludes Approved / Cancelled.
  const statuses = Array.from(BALL_STATUSES)
  const ors = statuses.map(s => "{Status}='" + s + "'").join(',')
  // Also include reviews with no status set yet (brand new — ball is ours).
  const formula = 'OR(' + ors + ",{Status}=BLANK())"
  const records = await apiGetAll(
    TBL_REVIEWS,
    'filterByFormula=' + encodeURIComponent(formula) + '&maxRecords=500'
  )
  return records.map(normalizeCrossProjectReview)
}

function normalizeCrossProjectReview(r) {
  const base = normalizeReview(r)
  const f = r.fields || {}
  return {
    ...base,
    projectId:     first(f['Project Record UID (from Project) (from Permits)']) || null,
    projectName:   first(f['Project Name and Sub Name (from Permits)']) || '',
    projectNumber: first(f['Project Number (from Project) (from Permits)']) || '',
  }
}

/**
 * Cross-project Permits board (Permitting workspace) — every Agency Review across every
 * project, not just the ones where we have the ball. `ballOnly:true` (default) matches
 * getBallInCourtReviews() exactly, so the board's default view is the same "who owes the next
 * move" list the /permits command already surfaces; `ballOnly:false` returns everything so the
 * board can offer a "show all" toggle.
 * @returns {Promise<object[]>} normalized reviews (with permitId + project info)
 */
export async function getAllAgencyReviews({ ballOnly = true } = {}) {
  if (ballOnly) return getBallInCourtReviews()
  const records = await apiGetAll(TBL_REVIEWS, 'maxRecords=1000')
  return records.map(normalizeCrossProjectReview)
}
